/**
 * Terms of Service Page Component
 * Renders the terms of service for public viewing
 */

import React from 'react';
import { Link } from 'react-router-dom';
import IconDaggerheart from '../common/IconDaggerheart';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-surface-1 text-gray-100">
      {/* Navigation — matches LandingPage */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-surface-1 p-4 flex items-center justify-between h-[73px]">
        <Link to="/" className="flex items-center space-x-3">
          <IconDaggerheart className="w-8 h-8 text-red-400" />
          <span className="text-2xl font-bold text-gray-100 tracking-wider font-serif">
            Chaggerheart
          </span>
        </Link>
        <Link to="/" className="text-gray-400 hover:text-red-400 transition-colors text-sm">
          ← Back to Home
        </Link>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 pt-24 pb-12">
        <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
        <p className="text-gray-400 mb-8">Effective Date: January 27, 2026 | Last Updated: January 27, 2026</p>

        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-lg text-gray-300">
            Welcome to Chaggerheart! These Terms of Service ("Terms") govern your use of the
            Chaggerheart application and related services (the "Service") operated by
            Chag Innovations LLC ("we," "us," or "our").
          </p>

          <div className="bg-gradient-to-br from-gray-900 to-black border border-red-700 rounded-lg p-4 text-gray-300">
            By accessing or using the Service, you agree to be bound by these Terms and our
            Privacy Policy. If you do not agree, please do not use the Service.
          </div>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">1. Description of Service</h2>
            <p className="text-gray-300">
              Chaggerheart is a companion application for tabletop role-playing games,
              specifically designed for use with Daggerheart. The Service provides character creation
              and management tools, campaign organization features, real-time collaboration tools,
              content generation features, and GM dashboard capabilities.
            </p>
            <p className="text-gray-400 mt-4">
              The Service uses content from the Daggerheart System Reference Document under the
              Darrington Press Community Gaming License (DPCGL).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">2. User Accounts</h2>

            <h3 className="text-xl font-semibold text-gray-200 mb-3">Eligibility</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>You must be at least 13 years old to use the Service</li>
              <li>If you are under 18, you must have parental consent</li>
              <li>You must provide accurate and complete registration information</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-200 mt-6 mb-3">Account Security</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>You are responsible for maintaining the confidentiality of your account</li>
              <li>You are responsible for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>One account per person; multiple accounts may be terminated</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-200 mt-6 mb-3">Account Termination</h3>
            <p className="text-gray-300">
              We may suspend or terminate your account if you violate these Terms, engage in
              fraudulent or illegal activity, abuse the Service or other users, or remain
              inactive for 12+ consecutive months (with prior notice).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">3. User Content</h2>

            <h3 className="text-xl font-semibold text-gray-200 mb-3">Your Content</h3>
            <p className="text-gray-300">
              You retain ownership of content you create in Chaggerheart, including character
              information, campaign notes, and custom descriptions.
            </p>

            <h3 className="text-xl font-semibold text-gray-200 mt-6 mb-3">License to Us</h3>
            <p className="text-gray-300">
              By using the Service, you grant us a non-exclusive, worldwide, royalty-free license
              to store and display your content as necessary to provide the Service. This license
              ends when you delete your content or account.
            </p>

            <h3 className="text-xl font-semibold text-gray-200 mt-6 mb-3">Prohibited Content</h3>
            <p className="text-gray-300 mb-4">You may not create, upload, or share content that is:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Illegal:</strong> Violates laws, promotes illegal activity, contains malware</li>
              <li><strong>Harmful:</strong> Harassment, hate speech, threats, self-harm promotion</li>
              <li><strong>Inappropriate:</strong> Explicit sexual content, graphic violence</li>
              <li><strong>Infringing:</strong> Copyright/trademark violations, plagiarism</li>
              <li><strong>Deceptive:</strong> Spam, scams, phishing, impersonation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">4. Generated Content</h2>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Certain features generate content based on your inputs and game context</li>
              <li>Generated outputs are provided "as is" and may contain errors</li>
              <li>You are responsible for reviewing generated content before use</li>
              <li>Voice recording for session notes requires explicit opt-in</li>
              <li>Usage limits may apply to certain generation features</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">5. Subscriptions and Payments</h2>
            <p className="text-gray-300 mb-4">
              Chaggerheart offers a free tier with core features and optional paid tiers with
              additional capabilities. Pricing details will be published before any paid features launch.
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>You may cancel your subscription at any time</li>
              <li>No refunds for partial billing periods</li>
              <li>You retain access to paid features until the current period ends</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">6. Intellectual Property</h2>
            <p className="text-gray-300 mb-4">
              Chaggerheart, including its name, logo, software, and design, is the property of
              Chag Innovations LLC. You may not copy, modify, or distribute our software without permission.
            </p>
            <p className="text-gray-300">
              Daggerheart game content used in Chaggerheart is licensed under the Darrington Press
              Community Gaming License. Daggerheart and Darrington Press are trademarks of
              Darrington Press, LLC. We are not affiliated with or endorsed by Darrington Press.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">7. Beta Program</h2>
            <p className="text-gray-300 mb-4">During the closed beta period:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>The Service is provided in beta form and may contain bugs</li>
              <li>Features may change, be added, or removed without notice</li>
              <li>Data may be reset in extreme circumstances (with advance notice when possible)</li>
              <li>Beta testers receive 6 months free access at launch</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">8. Disclaimers</h2>
            <div className="bg-gradient-to-br from-gray-900 to-black border border-red-700 rounded-lg p-4 text-gray-300 uppercase text-sm">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-300 mb-4">
              To the maximum extent permitted by law, we are not liable for any indirect, incidental,
              special, consequential, or punitive damages, or loss of profits, data, or goodwill.
            </p>
            <p className="text-gray-300">
              Our total liability shall not exceed the greater of the amount you paid us in the
              12 months preceding the claim, or $100 USD.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">10. Dispute Resolution</h2>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Before filing any claim, contact contact@chaggerheart.com to attempt informal resolution</li>
              <li>These Terms are governed by the laws of the United States</li>
              <li>You agree to resolve disputes individually and waive class action participation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">11. Changes to Terms</h2>
            <p className="text-gray-300">
              We may update these Terms from time to time. We will notify you of material changes
              via email or in-app notification. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">12. Contact Information</h2>
            <p className="text-gray-300">
              For questions about these Terms or support issues: <strong>contact@chaggerheart.com</strong>
            </p>
          </section>
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-gray-700/50">
          <div className="text-center text-sm text-gray-500 mb-6">
            <p className="mb-2">Compatible with Daggerheart.</p>
            <p>
              This application uses content from the Daggerheart System Reference Document
              under the Darrington Press Community Gaming License. Daggerheart and Darrington Press
              are trademarks of Darrington Press, LLC.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-6">
            <Link to="/about" className="hover:text-red-400 transition-colors">About</Link>
            <Link to="/privacy" className="hover:text-red-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-red-400 transition-colors">Terms of Service</Link>
            <a href="https://discord.gg/uYf6WCQ4z7" target="_blank" rel="noopener noreferrer" className="hover:text-red-400 transition-colors">Discord</a>
          </div>
          <div className="text-center text-xs text-gray-600">
            © 2025-2026 Chag Innovations LLC. All rights reserved.
          </div>
        </footer>
      </main>
    </div>
  );
}
